import type { ImageData } from 'shared/components/ui/optimized-image/optimized-image';
import { MovesEnum } from 'shared/constants/shared-enums';

// None
import nonePaper from 'assets/moves/me-paper.png?format=webp;avif&as=picture';
import noneRock from 'assets/moves/me-rock.png?format=webp;avif&as=picture';
import noneScissors from 'assets/moves/me-scissors.png?format=webp;avif&as=picture';
// Bronze
import brPaper from 'assets/nft/br-p.png?format=webp;avif&as=picture';
import brRock from 'assets/nft/br-r.png?format=webp;avif&as=picture';
import brScissors from 'assets/nft/br-s.png?format=webp;avif&as=picture';
import brBack from 'assets/nft/br.png?format=webp;avif&as=picture';
// Diamond
import dPaper from 'assets/nft/d-p.png?format=webp;avif&as=picture';
import dRock from 'assets/nft/d-r.png?format=webp;avif&as=picture';
import dScissors from 'assets/nft/d-s.png?format=webp;avif&as=picture';
import dBack from 'assets/nft/d.png?format=webp;avif&as=picture';
// Gold
import gPaper from 'assets/nft/g-p.png?format=webp;avif&as=picture';
import gRock from 'assets/nft/g-r.png?format=webp;avif&as=picture';
import gScissors from 'assets/nft/g-s.png?format=webp;avif&as=picture';
import gBack from 'assets/nft/g.png?format=webp;avif&as=picture';
// Grandmaster
import gmPaper from 'assets/nft/gm-p.png?format=webp;avif&as=picture';
import gmRock from 'assets/nft/gm-r.png?format=webp;avif&as=picture';
import gmScissors from 'assets/nft/gm-s.png?format=webp;avif&as=picture';
import gmBack from 'assets/nft/gm.png?format=webp;avif&as=picture';
// Master
import mPaper from 'assets/nft/m-p.png?format=webp;avif&as=picture';
import mRock from 'assets/nft/m-r.png?format=webp;avif&as=picture';
import mScissors from 'assets/nft/m-s.png?format=webp;avif&as=picture';
import mBack from 'assets/nft/m.png?format=webp;avif&as=picture';
// Platinum
import plPaper from 'assets/nft/pl-p.png?format=webp;avif&as=picture';
import plRock from 'assets/nft/pl-r.png?format=webp;avif&as=picture';
import plScissors from 'assets/nft/pl-s.png?format=webp;avif&as=picture';
import plBack from 'assets/nft/pl.png?format=webp;avif&as=picture';
// Silver
import sPaper from 'assets/nft/s-p.png?format=webp;avif&as=picture';
import sRock from 'assets/nft/s-r.png?format=webp;avif&as=picture';
import sScissors from 'assets/nft/s-s.png?format=webp;avif&as=picture';
import sBack from 'assets/nft/s.png?format=webp;avif&as=picture';

import { Tiers } from './types';

/* ===== Keys & orders (single source of truth) ===== */
export const ORDER = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'diamond',
  'epic',
  'legendary',
] as const;

export type TierKey = 'none' | (typeof ORDER)[number];

export type MoveKey = 'paper' | 'rock' | 'scissors' | 'back';

export const MOVE_STRINGS: Record<Exclude<MovesEnum, MovesEnum.None>, MoveKey> = {
  [MovesEnum.Rock]: 'rock',
  [MovesEnum.Paper]: 'paper',
  [MovesEnum.Scissors]: 'scissors',
};

export const MOVES = [MovesEnum.Rock, MovesEnum.Paper, MovesEnum.Scissors];

export const NFT_IMAGES: Record<
  TierKey,
  Record<Exclude<MovesEnum, MovesEnum.None>, ImageData> & { back: ImageData | null }
> = {
  none: {
    [MovesEnum.Rock]: noneRock,
    [MovesEnum.Paper]: nonePaper,
    [MovesEnum.Scissors]: noneScissors,
    back: null,
  },
  bronze: {
    [MovesEnum.Rock]: brRock,
    [MovesEnum.Paper]: brPaper,
    [MovesEnum.Scissors]: brScissors,
    back: brBack,
  },
  silver: {
    [MovesEnum.Rock]: sRock,
    [MovesEnum.Paper]: sPaper,
    [MovesEnum.Scissors]: sScissors,
    back: sBack,
  },
  gold: {
    [MovesEnum.Rock]: gRock,
    [MovesEnum.Paper]: gPaper,
    [MovesEnum.Scissors]: gScissors,
    back: gBack,
  },
  platinum: {
    [MovesEnum.Rock]: plRock,
    [MovesEnum.Paper]: plPaper,
    [MovesEnum.Scissors]: plScissors,
    back: plBack,
  },
  diamond: {
    [MovesEnum.Rock]: dRock,
    [MovesEnum.Paper]: dPaper,
    [MovesEnum.Scissors]: dScissors,
    back: dBack,
  },
  epic: {
    [MovesEnum.Paper]: mPaper,
    [MovesEnum.Rock]: mRock,
    [MovesEnum.Scissors]: mScissors,
    back: mBack,
  },
  legendary: {
    [MovesEnum.Rock]: gmRock,
    [MovesEnum.Paper]: gmPaper,
    [MovesEnum.Scissors]: gmScissors,
    back: gmBack,
  },
};

export const collectionStyle = {
  bronze:
    'bg-[linear-gradient(179deg,_#FFF_-12.42%,_#FFDE7A_116.87%)] drop-shadow-[0_2px_0_#481700] sm:drop-shadow-[0_4px_0_#481700] [-webkit-text-stroke-color:#8F2D00]',
  silver:
    'bg-[linear-gradient(179deg,_#FFF_-12.42%,_#7AD3FF_116.87%)] drop-shadow-[0_2px_0_#0C354C] sm:drop-shadow-[0_4px_0_#0C354C] [-webkit-text-stroke-color:#165070]',
  gold: 'bg-[linear-gradient(179deg,_#FFF_-12.42%,_#FFD37A_116.87%)] drop-shadow-[0_2px_0_#4C280C] sm:drop-shadow-[0_4px_0_#4C280C]  [-webkit-text-stroke-color:#703116]',
  platinum:
    'bg-[linear-gradient(179deg,_#FFF_-12.42%,_#7BC7A2_116.87%)] drop-shadow-[0_2px_0_#23423E] sm:drop-shadow-[0_4px_0_#23423E]  [-webkit-text-stroke-color:#4B6C67]',
  diamond:
    'bg-[linear-gradient(179deg,_#FFF_-12.42%,_#9DFFFF_116.87%)] drop-shadow-[0_2px_0_#002C52] sm:drop-shadow-[0_4px_0_#002C52]  [-webkit-text-stroke-color:#0C5599]',
  epic: 'bg-[linear-gradient(179deg,_#FFF_-12.42%,_#CF5665_116.87%)] drop-shadow-[0_2px_0_#3F0001] sm:drop-shadow-[0_4px_0_#3F0001]  [-webkit-text-stroke-color:#920000]',
  legendary:
    'bg-[linear-gradient(179deg,_#FFF_-12.42%,_#7AABFF_116.87%)] drop-shadow-[0_2px_0_#100A63] sm:drop-shadow-[0_4px_0_#100A63]  [-webkit-text-stroke-color:#231A9D]',
};

export const MAX_FORGE_BATCH = 9;

export const TIER_ID: Record<
  Exclude<TierKey, 'none'>,
  Record<Exclude<MovesEnum, MovesEnum.None>, Tiers>
> = {
  bronze: {
    [MovesEnum.Rock]: Tiers.BronzeRock,
    [MovesEnum.Paper]: Tiers.BronzePaper,
    [MovesEnum.Scissors]: Tiers.BronzeScissors,
  },
  silver: {
    [MovesEnum.Rock]: Tiers.SilverRock,
    [MovesEnum.Paper]: Tiers.SilverPaper,
    [MovesEnum.Scissors]: Tiers.SilverScissors,
  },
  gold: {
    [MovesEnum.Rock]: Tiers.GoldRock,
    [MovesEnum.Paper]: Tiers.GoldPaper,
    [MovesEnum.Scissors]: Tiers.GoldScissors,
  },
  platinum: {
    [MovesEnum.Rock]: Tiers.PlatinumRock,
    [MovesEnum.Paper]: Tiers.PlatinumPaper,
    [MovesEnum.Scissors]: Tiers.PlatinumScissors,
  },
  diamond: {
    [MovesEnum.Rock]: Tiers.DiamondRock,
    [MovesEnum.Paper]: Tiers.DiamondPaper,
    [MovesEnum.Scissors]: Tiers.DiamondScissors,
  },
  epic: {
    [MovesEnum.Rock]: Tiers.MasterRock,
    [MovesEnum.Paper]: Tiers.MasterPaper,
    [MovesEnum.Scissors]: Tiers.MasterScissors,
  },
  legendary: {
    [MovesEnum.Rock]: Tiers.GrandmasterRock,
    [MovesEnum.Paper]: Tiers.GrandmasterPaper,
    [MovesEnum.Scissors]: Tiers.GrandmasterScissors,
  },
} as const;

// Mint card interface
export interface MintCard {
  image: ImageData;
  move: MovesEnum;
  tierId: number;
}

// Forge card interface
export interface ForgeCard {
  imageFrom: ImageData;
  imageTo: ImageData;
  move: MovesEnum;
  collection: TierKey;
  prevCollection: TierKey;
  prevCount: number;
  tierId: number;
}
